import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { TWO_HOURS_IN_MILLISECONDS, TWO_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import type { ApiClient } from '../generated/laravel-typed-openapi';
import { publicChecklistKeys } from './publicChecklistKeys';
import { fetchFeaturedPublicChecklists, fetchPublicChecklist, fetchPublicChecklists } from './publicChecklistsApi';
import { toPublicChecklistsQuery } from './queryParams';
import type { PublicChecklistsQueryParams } from './types';

/**
 * Query options for the public library, shared by the dashboard and the mobile app.
 *
 * They are options rather than hooks so each app can pick its own flavour of the same request —
 * mobile suspends, the dashboard doesn't — without either of them restating the cache policy. The
 * defaults carry over from the GraphQL hook these replace: a public library is somebody else's
 * content and barely changes between two glances at it, so two minutes fresh and two hours cached,
 * and no refetch when the window regains focus.
 */
const publicChecklistDefaults = {
  staleTime: TWO_MINUTES_IN_MILLISECONDS,
  gcTime: TWO_HOURS_IN_MILLISECONDS,
  refetchOnWindowFocus: false,
} as const;

/** One page, addressed by page number — the dashboard's table and card grid. */
export const publicChecklistsQueryOptions = (apiClient: ApiClient, params: PublicChecklistsQueryParams) => {
  const query = toPublicChecklistsQuery(params);

  return queryOptions({
    queryKey: publicChecklistKeys.list(query),
    queryFn: ({ signal }) => fetchPublicChecklists(apiClient, query, { signal }),
    // The page stays on screen while the next one loads, so paging and sorting don't blank the table.
    placeholderData: keepPreviousData,
    ...publicChecklistDefaults,
  });
};

/**
 * The same list as an endless scroll — the mobile screen.
 *
 * `page` is dropped from the key: it is the cursor, and leaving it in would give every page its own
 * cache entry instead of appending to one.
 */
export const publicChecklistsInfiniteQueryOptions = (
  apiClient: ApiClient,
  { page: _page, ...params }: PublicChecklistsQueryParams,
) => {
  const query = toPublicChecklistsQuery(params);

  return infiniteQueryOptions({
    queryKey: publicChecklistKeys.infiniteList(query),
    queryFn: ({ pageParam, signal }) => fetchPublicChecklists(apiClient, { ...query, page: pageParam }, { signal }),
    initialPageParam: 1,
    getNextPageParam: ({ meta }) => (meta.hasMorePages ? meta.currentPage + 1 : undefined),
    ...publicChecklistDefaults,
  });
};

/** The curated shelf. Same envelope, different endpoint. */
export const featuredPublicChecklistsQueryOptions = (apiClient: ApiClient, params: PublicChecklistsQueryParams) => {
  const query = toPublicChecklistsQuery(params);

  return queryOptions({
    queryKey: publicChecklistKeys.featuredList(query),
    queryFn: ({ signal }) => fetchFeaturedPublicChecklists(apiClient, query, { signal }),
    placeholderData: keepPreviousData,
    ...publicChecklistDefaults,
  });
};

/**
 * One public checklist with its items.
 *
 * A uuid that names nothing resolves to `null` rather than throwing, so the details screen can say
 * "no longer available" instead of falling into an error boundary. A request that fails — offline,
 * or a 500 — still rejects, because that is a different thing and reads differently on screen.
 */
export const publicChecklistQueryOptions = (apiClient: ApiClient, uuid: string) =>
  queryOptions({
    queryKey: publicChecklistKeys.detail(uuid),
    queryFn: ({ signal }) => fetchPublicChecklist(apiClient, uuid, { signal }),
    ...publicChecklistDefaults,
  });
